import { useRef, useEffect, useState } from 'react'
import { Globe, ChevronDown } from '@digital-twins/design-system/icons'
import { ROOM_LANGUAGES } from '@/lib/constants/languages'
import { cn } from '@/lib/utils'

interface HomeLanguagePickerProps {
  value: string
  onChange: (code: string) => void
  disabled?: boolean
}

export function HomeLanguagePicker({ value, onChange, disabled = false }: HomeLanguagePickerProps) {
  const selectRef = useRef<HTMLSelectElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  const [selectWidth, setSelectWidth] = useState<number | undefined>(undefined)

  const currentLabel = ROOM_LANGUAGES.find((l) => l.code === value)?.name ?? 'English'

  useEffect(() => {
    if (measureRef.current) {
      const textWidth = measureRef.current.offsetWidth
      setSelectWidth(textWidth + 42)
    }
  }, [currentLabel])

  return (
    <div className={cn('relative inline-flex items-center', disabled && 'opacity-50 pointer-events-none')}>
      <span
        ref={measureRef}
        className="pointer-events-none absolute left-0 top-0 -z-10 whitespace-nowrap text-xs font-medium opacity-0"
        aria-hidden="true"
      >
        {currentLabel}
      </span>
      <select
        ref={selectRef}
        data-testid="home-language-picker"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={selectWidth ? { width: `${selectWidth}px` } : undefined}
        className={cn(
          'appearance-none cursor-pointer rounded-md py-1 pl-6 pr-4 text-xs font-medium transition-colors',
          'bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50',
          'focus:outline-none focus:ring-0',
        )}
      >
        {ROOM_LANGUAGES.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.name}
          </option>
        ))}
      </select>
      <Globe className="pointer-events-none absolute left-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
      <ChevronDown className="pointer-events-none absolute right-0.5 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-muted-foreground" />
    </div>
  )
}
